import { AsyncPipe, CommonModule, NgForOf, NgIf } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { getHttpErrorMessage } from "../../../shared/utils/http-error.util";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { RouterLink } from "@angular/router";
import { TuiDay, TuiValidationError } from "@taiga-ui/cdk";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiError,
  TuiLink,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBreadcrumbs,
  TuiDataListWrapper,
  TuiFade,
  TuiFieldErrorPipe,
  TuiFiles,
  TuiFilterByInputPipe,
  TuiStringifyContentPipe,
  TuiTabs,
} from "@taiga-ui/kit";
import { TuiCell, TuiNavigation } from "@taiga-ui/layout";
import {
  TuiComboBoxModule,
  TuiInputDateModule,
  TuiInputModule,
  TuiInputPhoneModule,
  TuiMultiSelectModule,
  TuiSelectModule,
  TuiTextareaModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { catchError, of } from "rxjs";
import { ClientService } from "../../../core/services/client.service";
import { formatClientIdentifier } from "../../../shared/utils/client-identifier";
import { TuiTable } from "@taiga-ui/addon-table";
import { InvoiceService } from "../../../core/services/invoice.service";
import { DisableUnlessPermissionDirective } from "../../../core/directives/disable-unless-permission.directive";
import { EnterSubmitDirective } from "../../../core/directives/enter-submit.directive";
import {
  countInvalidFields,
  focusFirstInvalid,
} from "../../../shared/utils/form-focus";

@Component({
  selector: "app-genarate-invoice",
  imports: [
    DisableUnlessPermissionDirective,
    EnterSubmitDirective,
    FormsModule,
    CommonModule,
    NgForOf,
    NgIf,
    TuiAppearance,
    TuiBreadcrumbs,
    TuiButton,
    TuiDataList,
    TuiDropdown,
    TuiFade,
    TuiLink,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiFiles,
    TuiFieldErrorPipe,
    TuiError,
    ReactiveFormsModule,
    AsyncPipe,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiTextareaModule,
    TuiInputPhoneModule,
    TuiComboBoxModule,
    TuiMultiSelectModule,
    TuiSelectModule,
    TuiDataListWrapper,
    RouterLink,
    TuiInputDateModule,
    TuiTable,
    TuiCell,
    TuiStringifyContentPipe,
    TuiFilterByInputPipe
  ],
  templateUrl: "./genarate-invoice.component.html",
  styleUrl: "./genarate-invoice.component.scss",
})
export class GenarateInvoiceComponent implements OnInit {
  breadcrumbs = [
    { label: "Royal Hospital" },
    { label: "Invoice", link: "/admin/invoice" },
    { label: "Generate Invoice" },
  ];

  clients: any[] = [];
  editingIndex: number | null = null;
  invoiceForm: FormGroup;
  isLoading = false;
  invoiceId: string | null = null;
  newInvoice: boolean = true;
  minDischargeDate: TuiDay = TuiDay.currentLocal();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private clientService: ClientService,
    private invoiceService: InvoiceService,
    private fb: FormBuilder,
    private alerts: TuiAlertService
  ) {
    this.invoiceForm = this.fb.group({
      client: ["", Validators.required],
      clientId: ["", Validators.required],
      email: [{ value: "", disabled: true }],
      phoneNumber: [{ value: "", disabled: true }],
      address: [{ value: "", disabled: true }],
      admissionDate: [null, Validators.required],
      dischargeDate: [null, Validators.required],
      // The backend requires `charges` and `amount` (invoice.model.ts), so an
      // empty invoice would 500 on submit. Add a small custom validator on
      // the FormArray so the soft-warn hint can render in the template and
      // the submit button stays disabled until at least one row exists.
      charges: this.fb.array(
        [],
        (arr: AbstractControl) => {
          const length = (arr as FormArray).length;
          return length === 0 ? { atLeastOne: true } : null;
        },
      ),
      amount: [""],
      status: ["Draft"],
      newCharge: this.fb.group({
        description: [""],
        quantity: [null, Validators.min(1)],
        unitPrice: [null, Validators.min(0)],
      }),
    });
  }

  ngOnInit(): void {
    this.loadClients();

    this.route.paramMap.subscribe((params) => {
      this.invoiceId = params.get("id");
      if (this.invoiceId) {
        this.loadInvoiceData(this.invoiceId);
        this.newInvoice = false;
        this.breadcrumbs = [
          { label: "Royal Hospital" },
          { label: "Invoice", link: "/admin/invoice" },
          { label: "Edit Invoice" },
        ];
      }
    });

    this.invoiceForm.get("client")?.valueChanges.subscribe((selectedClient) => {
      if (selectedClient) {
        const client =
          typeof selectedClient === "object"
            ? selectedClient
            : this.clients.find((c: any) => c._id === selectedClient);

        if (client) {
          // Pick a sensible "admission date" default based on the client type.
          // Inward          -> inwardDate
          // Unregistered    -> startingDate
          // Registered      -> registrationDate
          // Falls back to createdAt if the type-specific date isn't set.
          const admissionSource =
            client.clientType === "Inward"
              ? client.inwardDate
              : client.clientType === "Unregistered"
              ? client.startingDate
              : client.registrationDate;
          const admissionRaw = admissionSource || client.createdAt;
          const admissionDay = admissionRaw
            ? TuiDay.fromLocalNativeDate(new Date(admissionRaw))
            : null;

          this.invoiceForm.patchValue({
            clientId: client._id,
            email: client.email,
            phoneNumber: client.guardianContactNo,
            address: client.guardianAddress,
            admissionDate: admissionDay,
          });
        }
      }
    });

    this.invoiceForm.get("admissionDate")?.valueChanges.subscribe((admissionDate: TuiDay) => {
      if (admissionDate) {
        this.minDischargeDate = admissionDate;

        const dischargeControl = this.invoiceForm.get("dischargeDate");
        if (dischargeControl?.value && dischargeControl.value < admissionDate) {
          dischargeControl.setValue(null);
        }
      }
    });

  }

  loadInvoiceData(invoiceId: string): void {
    this.isLoading = true;
  
    this.invoiceService
      .getOneInvoiceById(invoiceId)
      .pipe(
        catchError((error) => {
          console.error("Error loading invoice data:", error);
          this.isLoading = false;
          return of(null);
        })
      )
      .subscribe((response: any) => {
        if (response) {
          const { clientId, admissionDate, dischargeDate, charges, status } = response;
          

          const client = this.clients.find((c: any) => c._id === clientId._id);
          
          if (client) {
            this.invoiceForm.patchValue({
              client: client,
              clientId: client._id,
              email: clientId.email,
              phoneNumber: clientId.guardianContactNo,
              address: clientId.guardianAddress,
              admissionDate: admissionDate
                ? TuiDay.fromLocalNativeDate(new Date(admissionDate))
                : null,
              dischargeDate: dischargeDate
                ? TuiDay.fromLocalNativeDate(new Date(dischargeDate))
                : null,
              status,
            });

            this.invoiceForm.get('client')?.disable();
          }
  
          if (charges && charges.length > 0) {
            this.charges.clear(); 
            charges.forEach((charge: any) => {
              const chargeGroup = this.fb.group({
                description: [charge.description, Validators.required],
                quantity: [
                  charge.quantity,
                  [Validators.required, Validators.min(1)],
                ],
                unitPrice: [
                  charge.unitPrice,
                  [Validators.required, Validators.min(0)],
                ],
                total: [
                  { value: charge.quantity * charge.unitPrice, disabled: true },
                ],
              });
              this.charges.push(chargeGroup);
            });
          }
        }
        this.isLoading = false;
      });
  }

  /**
   * Soft-warn flexibility: include inactive/archived clients in the picker
   * (off by default). Allows historical billing entries; we surface a
   * warning when the chosen client is in either state.
   */
  includeInactiveClients: boolean = false;

  toggleIncludeInactiveClients(value: boolean): void {
    this.includeInactiveClients = value;
    this.loadClients();
  }

  isSelectedClientInactive(): boolean {
    const ctrl = this.invoiceForm.get("client");
    if (!ctrl?.value) return false;
    const id =
      typeof ctrl.value === "object" ? ctrl.value._id : ctrl.value;
    const client = this.clients.find((c: any) => c._id === id);
    if (!client) return false;
    return Boolean(client.archived) || client.active === false;
  }

  loadClients(): void {
    this.clientService.getAllClients().subscribe({
      next: (response: any) => {
        const clients = Array.isArray(response) ? response : [];
        this.clients = this.includeInactiveClients
          ? clients
          : clients.filter(
              (c: any) => !c?.archived && c?.active !== false,
            );
      },
      error: (error) => {
        console.error("Error fetching clients:", error);
      },
    });
  }

  stringify(client: any): string {
    if (!client) return "";
    if (typeof client === "object") {
      return `${formatClientIdentifier(client)} - ${client.name}`;
    }
    const foundClient = this.clients.find((c: any) => c._id === client);
    return foundClient
      ? `${formatClientIdentifier(foundClient)} - ${foundClient.name}`
      : "";
  }

  navigate(link?: string): void {
    if (link) {
      this.router.navigate([link]);
    }
  }

  get charges(): FormArray {
    return this.invoiceForm.get("charges") as FormArray;
  }

  get newChargeGroup(): FormGroup {
    return this.invoiceForm.get("newCharge") as FormGroup;
  }

  onAddCharge() {
    if (this.newChargeGroup.invalid) {
      this.alerts.open("Not filled", {
        label: "Error",
        appearance: "negative",
      });
      return;
    }
    
    const { description, quantity, unitPrice } = this.newChargeGroup.value;
    const total = quantity * unitPrice;
    
    if (this.editingIndex !== null) {
      this.charges.at(this.editingIndex).patchValue({
        description,
        quantity,
        unitPrice,
        total
      });
      this.editingIndex = null;
    } else {
      const chargeGroup = this.fb.group({
        description: [description, Validators.required],
        quantity: [quantity, [Validators.required, Validators.min(1)]],
        unitPrice: [unitPrice, [Validators.required, Validators.min(0)]],
        total: [{ value: total, disabled: true }],
      });
      this.charges.push(chargeGroup);
      // Ensure the atLeastOne validator clears the error immediately.
      this.charges.updateValueAndValidity();
    }

    this.newChargeGroup.reset({
      description: "",
      quantity: null,
      unitPrice: null,
    });
  }
  

  onEditCharge(index: number): void {
    const charge = this.charges.at(index).value;
    
    this.newChargeGroup.patchValue({
      description: charge.description,
      quantity: charge.quantity,
      unitPrice: charge.unitPrice,
    });
  
    this.editingIndex = index;
  }

  get subtotal(): number {
    return this.charges.controls.reduce((acc, chargeGroup) => {
      const chargeTotal = chargeGroup.get("total")?.value || 0;
      return acc + chargeTotal;
    }, 0);
  }

  onRemoveCharge(index: number) {
    this.charges.removeAt(index);
    this.charges.updateValueAndValidity();
  }

  saveInvoice() {
    this.isLoading = true;
    if (this.invoiceForm.valid) {
      const formValue = this.invoiceForm.value;

      // Tui's date pickers return TuiDay (year/month/day with no timezone). The
      // backend expects a Date / ISO string, so convert before serializing.
      const toDate = (value: TuiDay | Date | null | undefined): Date | null => {
        if (!value) return null;
        if (value instanceof TuiDay) return value.toLocalNativeDate();
        if (value instanceof Date) return value;
        return new Date(value as any);
      };

      const admissionDate = toDate(formValue.admissionDate);
      const dischargeDate = toDate(formValue.dischargeDate);

      const createInvoicePayload = {
        clientId: formValue.clientId,
        admissionDate,
        dischargeDate,
        charges: formValue.charges.map((charge: any) => ({
          description: charge.description,
          quantity: charge.quantity,
          unitPrice: charge.unitPrice,
        })),
        amount: this.subtotal,
        status: formValue.status,
      };

      const updateInvoicePayload = {
        _id: this.invoiceId,
        clientId: formValue.clientId,
        admissionDate,
        dischargeDate,
        charges: formValue.charges.map((charge: any) => ({
          description: charge.description,
          quantity: charge.quantity,
          unitPrice: charge.unitPrice,
        })),
        amount: this.subtotal,
        status: formValue.status,
      };

      if (this.invoiceId) {
        this.invoiceService
          .updateInvoice(updateInvoicePayload)
          .pipe(
            catchError((error) => {
              this.isLoading = false;
              this.alerts
                .open(getHttpErrorMessage(error), {
                  label: "Error",
                  appearance: "negative",
                })
                .subscribe();
              return of(null);
            })
          )
          .subscribe((res: any) => {
            this.isLoading = false;
            if (res) {
              this.alerts
                .open("Invoice updated successfully", {
                  label: "Success",
                  appearance: "success",
                })
                .subscribe();
              this.router.navigateByUrl("/admin/invoice");
            }
          });
      } else {
        this.invoiceService
          .createInvoice(updateInvoicePayload)
          .pipe(
            catchError((error) => {
              this.isLoading = false;
              this.alerts
                .open(getHttpErrorMessage(error), {
                  label: "Error",
                  appearance: "negative",
                })
                .subscribe();
              return of(null);
            })
          )
          .subscribe((res: any) => {
            this.isLoading = false;
            if (res) {
              this.alerts
                .open("Invoice created successfully", {
                  label: "Success",
                  appearance: "success",
                })
                .subscribe();
              this.router.navigateByUrl("/admin/invoice");
            }
          });
      }
    } else {
      const count = countInvalidFields(this.invoiceForm);
      focusFirstInvalid(this.invoiceForm);
      this.alerts
        .open(
          `${count} field${count === 1 ? "" : "s"} need attention before saving.`,
          { label: "Form incomplete", appearance: "warning" },
        )
        .subscribe();
      this.isLoading = false;
    }
  }
}
